import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { WalletSummaryResponse } from '@fetmode/types';
import { useAuth } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';
import { formatCoins, formatUsdCents, formatUsername } from '@fetmode/utils';
import { Button } from '@fetmode/ui';
import { Link } from 'react-router-dom';

import { StartOnboardingCard } from '../onboarding/index.js';
import { useStudioModel } from '../studio/index.js';

export function DashboardPage(): ReactElement {
  const { api } = useAuth();
  const { profile, liveShow, isLoading, error } = useStudioModel();
  const [wallet, setWallet] = useState<WalletSummaryResponse | null>(null);
  const [hubError, setHubError] = useState<string | null>(null);

  const loadHubData = useCallback(async () => {
    if (!profile) {
      setWallet(null);
      return;
    }

    try {
      setWallet(await api.getWallet());
    } catch {
      setHubError(t('common.studio.dashboard.hubLoadError'));
    }
  }, [api, profile]);

  useEffect(() => {
    void loadHubData();
  }, [loadHubData]);

  if (isLoading) {
    return <p style={{ color: 'var(--fm-color-text-muted)' }}>{t('common.studio.dashboard.loading')}</p>;
  }

  return (
    <section>
      <h1 style={{ fontFamily: 'var(--fm-font-heading)', marginTop: 0 }}>
        {t('common.studio.settings.overviewTitle')}
      </h1>
      <p style={{ color: 'var(--fm-color-text-muted)', marginTop: 0 }}>
        {t('common.studio.settings.overviewDescription')}
      </p>

      {error ? <p className="fm-error">{error}</p> : null}
      {hubError ? <p className="fm-error">{hubError}</p> : null}

      {!profile || !profile.canBroadcast ? (
        <StartOnboardingCard profile={profile} />
      ) : (
        <>
          <div className="fm-card">
            <h2 style={{ marginTop: 0, fontSize: '1rem' }}>{t('common.studio.dashboard.statsTitle')}</h2>
            {liveShow ? (
              <p style={{ margin: '0 0 0.5rem', fontSize: '0.875rem' }}>
                {t('common.studio.dashboard.viewerCount').replace(
                  '{{count}}',
                  String(liveShow.viewerCount),
                )}
                {(liveShow.spyCount ?? 0) > 0
                  ? ` · ${t('common.studio.live.statsSpies')}: ${String(liveShow.spyCount)}`
                  : ''}
              </p>
            ) : null}
            {wallet ? (
              <>
                {wallet.earn ? (
                  <>
                    <p style={{ margin: '0 0 0.25rem', fontSize: '0.875rem' }}>
                      {t('common.studio.dashboard.earnAvailable')}:{' '}
                      {formatUsdCents(wallet.earn.availableCents)}
                    </p>
                    <p style={{ margin: '0 0 0.5rem', fontSize: '0.875rem', color: 'var(--fm-color-text-muted)' }}>
                      {t('common.studio.dashboard.earnPending')}: {formatUsdCents(wallet.earn.pendingCents)}
                    </p>
                  </>
                ) : null}
                <p style={{ margin: '0 0 1rem', fontSize: '0.875rem', color: 'var(--fm-color-text-muted)' }}>
                  {t('common.studio.dashboard.spendBalance')}: {formatCoins(wallet.spend.total)}
                </p>
              </>
            ) : (
              <p style={{ margin: '0 0 1rem', color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
                {t('common.studio.dashboard.statsLoading')}
              </p>
            )}
            <Link to="/settings/analytics">
              <Button label={t('common.studio.dashboard.openAnalytics')} variant="secondary" />
            </Link>
          </div>

          <p style={{ color: 'var(--fm-color-text-muted)' }}>
            {t('common.studio.dashboard.welcome').replace(
              '{{name}}',
              profile.displayName || formatUsername(profile.username),
            )}
          </p>
        </>
      )}
    </section>
  );
}
